
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Footer from '../../../components/feature/Footer';
import TableOfContents, { generateTocItems } from '../../../components/feature/TableOfContents';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const tocItems = generateTocItems([
  'How El Paso Property Taxes Work',
  'Current El Paso Property Tax Rates by Taxing Entity',
  'Exemptions That Lower Your Tax Bill',
  'How Property Taxes Affect Home Affordability',
  'How to Protest Your El Paso Property Tax Appraisal',
  'Tips to Maximize Your Protest Success',
  'When High Property Taxes Make Selling the Smart Move',
  'Frequently Asked Questions',
  'Take Control of Your Property Tax Bill',
]);

export default function ElPasoPropertyTaxRatesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              src="https://public.readdy.ai/ai/img_res/c0e6113e-28a2-41ed-b2dc-22e3f9d83ef9.png"
              alt="Cash For Houses El Paso"
              className="h-12 w-auto"
            />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            <Link to="/get-cash" className="text-sm font-medium text-gray-700 hover:text-black transition-colors whitespace-nowrap cursor-pointer">Blog</Link>
            <a href="tel:9157457883" className="px-6 py-2.5 bg-black text-white text-sm font-semibold rounded-full hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer">Call: 915-745-7883</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-8">
            <Link to="/get-cash" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-black transition-colors cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </Link>
          </div>

          <div className="mb-6">
            <span className="inline-block px-4 py-2 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full">PROPERTY TAXES</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6 leading-tight">
            El Paso Property Tax Rates: How They Affect Affordability &amp; How to Protest
          </h1>

          <div className="flex items-center gap-6 text-sm text-gray-600 mb-10">
            <div className="flex items-center gap-2">
              <i className="ri-user-line"></i>
              <span>Cash For Houses El Paso</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-time-line"></i>
              <span>22 min read</span>
            </div>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-xl mb-12 h-96">
            <img
              src="https://public.readdy.ai/ai/img_res/ab64fb418fe1c6c5832469aa231f5c0c.jpg"
              alt="El Paso property tax assessment documents and appraisal notice on desk"
              title="El Paso property tax rates guide - understanding your tax bill and how to protest"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16" itemScope itemType="https://schema.org/Article">
        <meta itemProp="headline" content="El Paso Property Tax Rates: How They Affect Affordability and How to Protest" />
        <meta itemProp="author" content="Cash For Houses El Paso" />
        <div className="max-w-3xl mx-auto px-6">
          <TableOfContents items={tocItems} />
          <ArticlePartOne />
          <ArticlePartTwo />
          <ArticlePartThree />
          <ArticlePartFour />
        </div>
      </article>

      {/* Related Posts */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-black mb-10">Related Articles</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/market-data-el-paso-texas" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=stunning%20aerial%20view%20of%20El%20Paso%20Texas%20housing%20market%20with%20residential%20neighborhoods%20stretching%20toward%20Franklin%20Mountains%20at%20golden%20hour%2C%20warm%20desert%20sunset%20colors%20illuminating%20diverse%20communities%20and%20real%20estate%20developments%2C%20professional%20drone%20photography%20showing%20the%20scale%20of%20the%20border%20city%20housing%20landscape%20with%20mountain%20silhouettes%20and%20clear%20sky&width=600&height=400&seq=market-data-ep-hero-001&orientation=landscape"
                  alt="El Paso housing market data"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">MARKET DATA</span>
                <h3 className="text-xl font-semibold text-black mb-2">El Paso Housing Market Data &amp; Trends (2025)</h3>
                <p className="text-sm text-gray-600">Comprehensive overview of home values, pricing, and market forecasts.</p>
              </div>
            </Link>

            <Link to="/get-cash/financing-house-el-paso-tx" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20mortgage%20broker%20or%20loan%20officer%20reviewing%20home%20financing%20documents%20with%20first%20time%20homebuyers%20at%20modern%20desk%2C%20calculator%20and%20financial%20paperwork%20spread%20out%20showing%20loan%20approval%20process%2C%20warm%20welcoming%20office%20environment%20with%20natural%20lighting%2C%20El%20Paso%20Texas%20real%20estate%20financing%20consultation%2C%20optimistic%20atmosphere%20of%20successful%20home%20purchase%20journey&width=600&height=400&seq=financing-hero-ep-002&orientation=landscape"
                  alt="Financing a house in El Paso"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">FINANCING GUIDE</span>
                <h3 className="text-xl font-semibold text-black mb-2">Quick Guide to Financing Your House in El Paso</h3>
                <p className="text-sm text-gray-600">Understanding mortgage options and how to save thousands.</p>
              </div>
            </Link>

            <Link to="/get-cash/cant-pay-mortgage-el-paso" className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=stressed%20homeowner%20sitting%20at%20kitchen%20table%20with%20mortgage%20bills%20and%20past%20due%20notices%20spread%20out%2C%20worried%20expression%20looking%20at%20financial%20documents%2C%20warm%20natural%20lighting%20through%20window%2C%20realistic%20documentary%20photography%20of%20financial%20hardship%20moment%2C%20El%20Paso%20Texas%20home%20interior&width=600&height=400&seq=mortgage-blog-001&orientation=landscape"
                  alt="Can't pay mortgage in El Paso"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-[#D4AF37] text-white text-xs uppercase font-semibold rounded-full mb-3">FORECLOSURE PREVENTION</span>
                <h3 className="text-xl font-semibold text-black mb-2">Can&#39;t Pay the Mortgage? What Happens in El Paso</h3>
                <p className="text-sm text-gray-600">Learn what happens when you miss payments and how to stop foreclosure.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2C2C2C] py-20">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Property Taxes Too High? Get a Cash Offer Today.</h2>
          <p className="text-xl text-white/80 mb-8">Sell your El Paso home fast—no repairs, no commissions, no waiting</p>

          <a href="tel:9157457883" className="inline-block text-5xl font-bold text-white mb-8 hover:text-[#D4AF37] transition-colors cursor-pointer">
            915-745-7883
          </a>

          <div>
            <Link to="/#contact-form" className="inline-block px-12 py-4 bg-white text-black text-lg font-semibold rounded-full hover:scale-105 hover:shadow-2xl transition-all whitespace-nowrap cursor-pointer">
              Get Your Cash Offer
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
